import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const revenueOptions = [
  "Pre-revenue",
  "< €500k",
  "€500k – €2M",
  "€2M – €10M",
  "€10M – €50M",
  "€50M+",
];

const amazonPresenceOptions = [
  "Not on Amazon at all",
  "On Amazon.co.uk only",
  "On Amazon.com only",
  "On 1–2 EU marketplaces",
  "On all EU marketplaces — needs help scaling",
];

export default function Contact() {
  const [form, setForm] = useState({
    brand: "", name: "", role: "", email: "",
    annual_revenue: "", amazon_presence: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brand) {
      toast.error("Please share at minimum your brand, name, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        company: form.brand,
        monthly_revenue: form.annual_revenue,
        marketplaces: form.amazon_presence ? [form.amazon_presence] : [],
        message: `Role: ${form.role}\n\n${form.message}`,
      });
      setDone(true);
      toast.success("Enquiry received. Response within 48 hours.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email hello@traeva.ie");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-[88px] md:pt-[72px]">
      <section className="border-b border-line">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-8 sm:pt-12 lg:pt-16 pb-16">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-5">
              <span className="w-9 h-px bg-coral" />
              Request a Bearing
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-serif-i italic text-[44px] sm:text-6xl lg:text-[80px] text-ink leading-[.98] tracking-[-.035em] max-w-5xl">
              Find out where your brand stands.
              <br />
              It costs <span className="text-coral">nothing.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-10 max-w-2xl text-[16px] sm:text-lg text-ink-muted leading-[1.78]">
              Send one line &mdash; your website address. We&rsquo;ll prepare a free Bearing: an honest
              read on your Amazon EU opportunity, ending in a stamp.{" "}
              <strong className="text-ink font-medium">No call. No obligation. Yours either way.</strong>
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-12 flex flex-wrap gap-4 items-center">
              <a
                href="mailto:hello@traeva.ie?subject=Bearing%20request&body=Brand%20website%3A%20"
                data-testid="contact-mailto-cta"
                className="inline-flex items-center gap-2 bg-coral text-white text-[14px] font-medium px-8 py-4 transition-transform hover:-translate-y-0.5 hover:bg-coral-dark"
              >
                Email hello@traeva.ie <ArrowRight size={16} strokeWidth={1.6} />
              </a>
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-low">
                Or use the form below
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-line">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Form column */}
            <div className="lg:col-span-8">
              {done ? (
                <div data-testid="contact-success" className="py-20">
                  <div className="w-12 h-12 bg-coral text-paper flex items-center justify-center">
                    <Check size={22} strokeWidth={1.8} />
                  </div>
                  <h2 className="mt-10 font-serif-i text-4xl lg:text-5xl text-ink">Enquiry received.</h2>
                  <p className="mt-6 max-w-xl text-ink-soft leading-relaxed">
                    Every enquiry is read personally. Expect a response within 48 hours at <span className="text-ink">{form.email}</span>.
                  </p>
                  <button
                    data-testid="contact-reset"
                    className="btn-ghost mt-12"
                    onClick={() => { setDone(false); setForm({ brand: "", name: "", role: "", email: "", annual_revenue: "", amazon_presence: "", message: "" }); }}
                  >
                    Send another <ArrowRight size={16} strokeWidth={1.6} />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
                  <Field label="Brand name" required>
                    <input
                      data-testid="contact-brand"
                      value={form.brand}
                      onChange={e => update("brand", e.target.value)}
                      className="input-field"
                      placeholder="The brand you operate"
                      required
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <Field label="Your name" required>
                      <input
                        data-testid="contact-name"
                        value={form.name}
                        onChange={e => update("name", e.target.value)}
                        className="input-field"
                        placeholder="Full name"
                        required
                      />
                    </Field>
                    <Field label="Your role">
                      <input
                        data-testid="contact-role"
                        value={form.role}
                        onChange={e => update("role", e.target.value)}
                        className="input-field"
                        placeholder="Founder, CMO, Head of eCommerce…"
                      />
                    </Field>
                  </div>

                  <Field label="Email address" required>
                    <input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={e => update("email", e.target.value)}
                      className="input-field"
                      placeholder="you@brand.ie"
                      required
                    />
                  </Field>

                  <Field label="Annual brand revenue">
                    <select
                      data-testid="contact-revenue"
                      value={form.annual_revenue}
                      onChange={e => update("annual_revenue", e.target.value)}
                      className="input-field appearance-none"
                    >
                      <option value="">Select a range…</option>
                      {revenueOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>

                  <Field label="Current Amazon presence">
                    <select
                      data-testid="contact-presence"
                      value={form.amazon_presence}
                      onChange={e => update("amazon_presence", e.target.value)}
                      className="input-field appearance-none"
                    >
                      <option value="">Select an option…</option>
                      {amazonPresenceOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>

                  <Field label="Tell us about your brand and goals">
                    <textarea
                      data-testid="contact-message"
                      rows={5}
                      value={form.message}
                      onChange={e => update("message", e.target.value)}
                      className="input-field resize-none"
                      placeholder="What you make, what's working, where you'd like to be in 12 months."
                    />
                  </Field>

                  <div className="pt-6">
                    <button
                      data-testid="contact-submit"
                      type="submit"
                      disabled={loading}
                      className="btn-primary disabled:opacity-50"
                    >
                      {loading ? "Sending…" : <>Send enquiry <ArrowRight size={16} strokeWidth={1.6} /></>}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Aside */}
            <aside className="lg:col-span-4">
              <div className="border-t border-line pt-8">
                <p className="label-mono">/ Direct</p>
                <ul className="mt-6 space-y-4">
                  <li><a href="mailto:hello@traeva.ie" data-testid="contact-info-email" className="text-ink hover:text-coral transition-colors">hello@traeva.ie</a></li>
                  <li><span className="text-ink-muted">traeva.ie</span></li>
                  <li><span className="text-ink-muted">Dublin, Ireland</span></li>
                </ul>
              </div>

              <div className="mt-16 border-t border-line pt-8" data-testid="contact-engagement-note">
                <p className="label-mono">/ Engagement</p>
                <p className="mt-6 font-serif-i text-2xl text-ink leading-snug">
                  Three brand partners per year.
                </p>
                <p className="mt-6 text-sm text-ink-muted leading-relaxed">
                  Assessed on brand strength, EU opportunity, and founder commitment.
                </p>
              </div>

              <div className="mt-16 border-t border-line pt-8">
                <p className="label-mono">/ Response</p>
                <p className="mt-6 font-serif-i text-2xl text-ink">Under 48 hours.</p>
                <p className="mt-6 text-sm text-ink-muted leading-relaxed">
                  Every enquiry is read personally by the founder.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-mono block mb-3">
        {label} {required && <span className="text-coral">*</span>}
      </span>
      {children}
    </label>
  );
}
